
import Prompt from '@utils/Prompt'
import axios from 'axios'

class BaseModel {

    /**
     * Primary key of the model
     *
     * @var {string}
     * @memberof BaseModel
     */
    primaryKey = 'ID'

    /**
     * The title of the model
     *
     * @description This title will be used to show dynamic name instead of changing the route name
     * @var {string}
     * @memberof BaseModel
     */
    title = ''

    /**
     * The data of the model
     */
    data = {}

    /**
     * Get the id of the model
     */
    get id() {
        return this.data[this.primaryKey] || null
    }

    /**
     * Set the id of the model
     */
    route = 'category'

    /**
     * The route of the model
     */
    constructor(data = {}) {
        if (typeof data === 'number') {
            this.data[this.primaryKey] = data
        }

        if (typeof data === 'object') {
            this.data = data
        }

        return this
    }

    /**
     * Static constructor
     *
     * @param {object} data
     * @returns
     */
    static init(data = {}) {
        return new this(data)
    }

    /**
     * Instance.
     *
     * @returns object
     */
    instance = () => {
        let source = typeof prebook_admin !== 'undefined' ? prebook_admin : prebook || {}

        return axios.create({
            baseURL: source?.url?.api || '/wp-json/prebook/',
            headers: {
                'Content-Type': 'application/json',
                'X-WP-Nonce': source?.rest_nonce || ''
            },
        })
    }

    /**
     * Get the data of the model
     */
    get = async (id = null) => {
        let item = await this.instance().get('/' + this.route + '/' + (id || this.id))

        if (item.data && item.data) {
            item.data = {
                ...item.data.data,
                update: this.update,
                delete: this.delete,
                publish: this.publish,
                unpublish: this.unpublish,
                archive: this.archive,
            }
        }
        return item.data
    }

    /**
     * Get all the data of the model
     */
    all = async () => {
        let items = await this.instance().get('/' + this.route)
        if (!items.data || !items.data.length) return []

        return items.data?.map(item => {
            return {
                ...item,
                update: this.update.bind(this, item[this.primaryKey]),
                delete: this.delete.bind(this, item[this.primaryKey]),
                deleteSilence: this.deleteSilence.bind(this, item[this.primaryKey]),
                publish: this.publish.bind(this, item[this.primaryKey]),
                unpublish: this.unpublish.bind(this, item[this.primaryKey])
            }
        })
    }

    /**
     * Update the data of the model
     */
    update = async (data, id = null) => {
        let updated = await this.instance().put('/' + this.route + '/' + (id || this.id), data)

        if (updated.data && updated.data) {
            return updated.data
        }

        return {
            success: false,
        }
    }

    /**
     * Request delete.
     * 
     * @param {number} id
     */
    delete = async (id = null) => {
        const choice = await Prompt.fire({
            title: 'Are you sure?',
            message: `Are you sure you want to delete the ${String(this.title).toLowerCase()}?`,
            ok: 'Yes, delete',
            cancel: 'No, cancel',
        })

        if (choice.ok) {
            let deleted = await this.deleteSilence(id)

            if (deleted.data && deleted.data) {
                return deleted.data
            }

            return {
                success: false,
            }
        }

        return false
    }

    /**
     * Delete the data of the model
     */
    deleteSilence = async (id = null) => {
        return await this.instance().delete('/' + this.route + '/' + (id || this.id))
    }

    /**
     * Create the data of the model
     */
    create = async (data) => {
        let response = await this.instance().post('/' + this.route, data)
        Log('response.data', response.data)
        return response.data
    }

    /**
     * Magic method to get the data of the model
     *
     * @param {string} name
     * @returns
     */
    __get(name) {
        return this.data[name]
    }

    /**
     * Magic method to set the data of the model
     *
     * @param {string} name
     * @param {*} value
     */
    __set(name, value) {
        this.data[name] = value
    }

    /**
     * GetData.
     *
     * @returns
     * @memberof BaseModel
     */
    getData() {
        return this.data
    }

    // Additional methods 

    /**
     * Publish
     *
     * @returns
     * @memberof BaseModel
     */
    async publish() {
        return await this.update({ status: 'publish' })
    }

    /**
     * Unpublish
     *
     * @returns
     * @memberof BaseModel
     */
    async unpublish() {
        return await this.update({ status: 'draft' })
    }

    /**
     * Archive
     */
    async archive() {
        return await this.update({ status: 'archive' })
    }

    /**
     * Bulk update
     */
    async bulkUpdate(ids, data = {}) {
        let response = await this.instance().put('/' + this.route + '/bulk', { ids, ...data })

        if (response.data && response.data) {
            return response.data
        }

        return {
            success: false,
        }
    }

    /**
     * Bulk delete
     */
    async bulkDelete(ids) {
        let response = await this.instance().delete('/' + this.route + '/bulk', {
            data: { ids }
        })

        if (response.data && response.data) {
            return response.data
        }

        return {
            success: false,
        }
    }

}

export default BaseModel